// Angular
import { Observable, takeUntil } from 'rxjs';
import { AfterViewInit, Component, Input, OnInit, ViewChild, HostListener, HostBinding, OnDestroy, Output, EventEmitter, OnChanges } from '@angular/core';

// Libraries
import { MatDrawer } from '@angular/material/sidenav';

import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { Select } from '@ngxs/store';

import { GridsterItem } from 'angular-gridster2';
import { TranslateService } from '@ngx-translate/core';

// Models
import { Job } from '../../models';
import { JobUpdate } from '../../models/job.model';
import { JobCommentDto } from '../../models/job-comment-dto';
import { JobAttachmentViewModel } from '../../models/job-attachment.model';
import { AppCleanup, DrawerModel } from '../../../../shared/models';
import { ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

// Services
import { JobService } from '../../services/job.service';
import { AuthService } from '../../../auth';
import { TimezoneService } from '../../services/timezone.service';
import { JobAttachmentService } from '../../services/job-attachment.service';
import { DialogService } from '../../../../shared/services/dialog.service';

// Pipes
import { MilitaryTimePipe } from '../../../../shared/pipes';

// Store
import { StorageState } from '../../../../shared/state/storage.state';

// Components
import { CommentAddDialogComponent } from '../daily-report/dialogs/comment-add-dialog/comment-add-dialog.component';

// Enums
import { Roles } from '../../../auth/models/roles.enum';
import { ChangeType } from '../../enums/change-type.enum';

// Helpers
import { DateHelper, UtilityHelper } from '../../../../shared/helpers';
import { DashboardService } from '../dashboard/services/dashboard.service';

@Component({
	selector: 'comment-log',
	templateUrl: './comment-log.component.html',
	styleUrls: ['./comment-log.component.scss'],
	providers: [MilitaryTimePipe],
	animations: [trigger('listAnimation', [transition('void => active', [style({ transform: 'translateX(100%)', opacity: 0 }), animate('1000ms', style({ transform: 'translateX(0)', opacity: 1 }))])])],
})
export class CommentLogComponent extends AppCleanup implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(StorageState.commentFontSize) commentFontSize$: Observable<string>;
	commentFontSize: number = 12;
	@Select(StorageState.commentDrawerSettings) commentDrawerSettings$: Observable<DrawerModel>;
	commentDrawerSettings: DrawerModel;
	isEditMode: boolean = false;

	@Input() jobKey: string;
	@Input() gridsterItem: GridsterItem;
	@Input() isEditable: boolean = false;
	@Input() isModal = false;
	@Input() isPublic = false;
	@Input() linkGuid: string;

	@Output() onDelete: EventEmitter<boolean> = new EventEmitter();
	@Output() onCopy = new EventEmitter();

	@ViewChild('drawer', { static: false }) drawer: MatDrawer;

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.isScreenLarge = window.innerWidth >= 1200;
	}

	jobCommentsOld: JobCommentDto[] = [];
	jobComments: JobCommentDto[] = [];
	data: Timeline2Data[] = [];
	isReady: boolean = false;
	job: Job;
	animationState = 'inactive';
	isAdmin = false;
	isScreenLarge = false;
	utcDateTimeOffset = null;
	isWidget: boolean = false;

	constructor(
		public jobService: JobService,
		private militaryTimePipe: MilitaryTimePipe,
		private dialog: MatDialog,
		private timezoneService: TimezoneService,
		private authService: AuthService,
		private jobAttachmentService: JobAttachmentService,
		private dialogService: DialogService,
		private dashboardService: DashboardService,
		private translateService: TranslateService
	) {
		super();
	}

	ngOnInit() {
		this.commentFontSize$.pipe(takeUntil(this.destroy)).subscribe((fontSize) => {
			if (fontSize) {
				let commentFontSize = fontSize;
				this.commentFontSize = parseInt(commentFontSize);
			}
		});

		this.commentDrawerSettings$.pipe(takeUntil(this.destroy)).subscribe((commentDrawerSettings) => {
			if (commentDrawerSettings) {
				this.commentDrawerSettings = UtilityHelper.deepCopy(commentDrawerSettings);
			}
		});

		// These are used to match Bootstrap resize settings
		this.isScreenLarge = window.innerWidth >= 1200;

		// NEW: Use job-specific stream instead of global stream with filtering
		this.jobService
			.getJobStream(this.jobKey)
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate && (jobUpdate.changeType === ChangeType.INITIAL || jobUpdate.changeType === ChangeType.JOB_COMMENT || jobUpdate.changeType === ChangeType.JOB_CHANGE) && jobUpdate.job.key === this.jobKey) {
					this.job = new Job(jobUpdate.job);
					this.utcDateTimeOffset = this.job.utcDateTimeOffset;
					this.jobCommentsOld = UtilityHelper.deepCopy(this.jobComments);

					if (this.job?.jobCommentReturn) {
						this.jobComments = UtilityHelper.deepCopy(this.job.jobCommentReturn.jobComments.filter((jc) => !jc.isDeleted && (!jc.hideInPortal || this.isAdmin)));
					} else {
						this.jobComments = [];
					}

					this.isReady = true;
					this.formComments();
				}
			});

		// LEGACY: Keep fallback for backward compatibility during migration
		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange && (jobChange.changeType === ChangeType.INITIAL || jobChange.changeType === ChangeType.JOB_COMMENT || jobChange.changeType === ChangeType.JOB_CHANGE) && jobChange.job.key === this.jobKey && !this.job) {
		// 		// Only fallback if job-specific stream hasn't loaded yet
		// 		this.job = new Job(jobChange.job);
		// 		this.utcDateTimeOffset = this.job.utcDateTimeOffset;
		// 		this.jobCommentsOld = UtilityHelper.deepCopy(this.jobComments);

		// 		if (this.job?.jobCommentReturn) {
		// 			this.jobComments = UtilityHelper.deepCopy(this.job.jobCommentReturn.jobComments.filter((jc) => !jc.isDeleted && (!jc.hideInPortal || this.isAdmin)));
		// 		} else {
		// 			this.jobComments = [];
		// 		}

		// 		this.isReady = true;
		// 		this.formComments();
		// 	}
		// });

		this.timezoneService
			.onTimezoneChanged()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result && result.changed === true) {
					this.utcDateTimeOffset = result.offset;
					this.data = [];
					this.jobComments.sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1));

					this.jobComments.forEach((jc) => {
						this.data.push({
							time: `${this.militaryTimePipe.transform(this.displayTimeZone(jc.commentDate))}`,
							date: DateHelper.format(jc.commentDate, 'M/d/yyyy'),
							icon: '',
							text: jc.mappedComment,
							user: jc?.createdByUser?.fullname,
							jobComment: jc,
						});
					});
				}
			});

		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin, Roles.Admin], () => {
				return true;
			})
			.pipe(takeUntil(this.destroy))
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		this.dashboardService.gridsterConfig$.pipe(takeUntil(this.destroy)).subscribe((gridsterConfig) => {
			if (gridsterConfig) {
				this.isEditMode = gridsterConfig?.draggable?.enabled;
			}
		});
	}

	ngOnChanges() {
		if (this.gridsterItem) {
			this.isWidget = true;
		}
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.animationState = 'active';
		}, 5000);
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	displayTimeZone(date: Date): Date {
		return this.timezoneService.timeZoneDate(date, this.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	formComments() {
		if (!UtilityHelper.isEqual(this.jobCommentsOld, this.jobComments)) {
			/*Below code is necessary because we can't replace the entire array and still have animations work
		We must look at each individual item and push/splice */
			this.jobComments.forEach((jc) => {
				if (!this.data.find((datum) => datum.jobComment.id === jc.id)) {
					this.data.push({
						time: `${this.militaryTimePipe.transform(this.displayTimeZone(jc.commentDate))}`,
						date: DateHelper.format(jc.commentDate, 'M/d/yyyy'),
						icon: '',
						text: jc.mappedComment,
						user: jc?.createdByUser?.fullname,
						jobComment: jc,
					});
				}
			});

			if (this.isPublic) {
				// Only show images to Public. Could filter this on back-end, but this was faster and API's are secure.
				this.jobComments.forEach((jc) => {
					jc.jobAttachments = jc.jobAttachments.filter((ja) => ja.isImage);
				});
			}

			// Removing non-existing comments
			var i = this.data.length;
			while (i--) {
				if (!this.jobComments.find((jc) => jc.id === this.data[i].jobComment.id)) {
					this.data.splice(i, 1);
				}
			}

			this.data = this.data.sort((a, b) => {
				return new Date(b.jobComment.commentDate).getTime() - new Date(a.jobComment.commentDate).getTime();
			});
		}
	}

	openCommentEditDialog(jobComment: JobCommentDto) {
		if (!this.isAdmin) {
			return;
		}

		const dialogRef = this.dialog.open(CommentAddDialogComponent, {
			data: {
				jobComment: jobComment,
				job: this.job,
				source: '',
			},
			width: '100%',
			maxWidth: this.isViewer ? '500px' : '450px',
			panelClass: 'comment-add-dialog-wrap',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result: { action: string; jobComment: JobCommentDto }) => {
				if (result) {
					if (result.action == 'insert') {
						this.jobService.addJobComment(this.jobKey, result.jobComment);
						let attachmentIds = result.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService.addJobAttachmentsToComment(this.job.id, result.jobComment.id, attachmentIds).subscribe((result) => {});
					} else if (result.action == 'update') {
						this.jobService.updateJobComment(this.jobKey, result.jobComment);
						let attachmentIds = result.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService.addJobAttachmentsToComment(this.job.id, result.jobComment.id, attachmentIds).subscribe((result) => {});
					} else if (result.action == 'delete') {
						this.jobService.deleteJobComment(this.jobKey, result.jobComment.id);
					}
				}
			});
	}

	openImageOrDownloadFile(jobAttachment: JobAttachmentViewModel) {
		this.jobAttachmentService.openImageOrDownloadFile(jobAttachment, this.isPublic, this.linkGuid);
	}

	deleteComment(comment: Timeline2Data) {
		const confirmModel: ConfirmDialogModel = {
			title: 'Delete comment',
			message: 'Are you sure you want to delete this comment?',
			action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response) {
					this.jobService.deleteJobComment(this.jobKey, comment.jobComment.id);
				}
			});
	}

	copyWidget() {
		this.onCopy.emit();
	}

	deleteWidget() {
		this.onDelete.emit(true);
	}

	getTooltipPlacement(data: any[], index: number) {
		let position: string;
		if (index === 0 || index === 1) {
			position = 'bottom-start';
		} else if ((index > 1 && index < data.length - 1) || (index > 1 && data.length <= 5)) {
			position = 'right';
		} else if (index > 1 && index === data.length - 1 && data.length > 5) {
			position = 'top-start';
		}

		return position;
	}
}

export interface Timeline2Data {
	time: string;
	date?: string;
	text: string;
	icon?: string;
	attachment?: string;
	user?: string;
	jobComment: JobCommentDto;
}
